import { cn } from '@virginexperiencedays/tailwind';
import type { HTMLAttributes } from 'react';
import { IconFiles } from '../icons/IconFiles';

export type MediaCount = 1 | 2 | 3 | 4;

const MAX_MEDIA_COUNT: MediaCount = 4;

type Props = {
  mediaCount: MediaCount;
};

export const MediaGrid = ({
  mediaCount,
  className,
  children,
  ...props
}: Props & HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn(
        'grid h-full w-full min-w-max gap-2 will-change-transform md:min-w-full',
        handleGridLayout(mediaCount),
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

type MediaGridItemProps = {
  mediaCount: MediaCount;
  itemPosition: number;
};

export const MediaGridItem = ({
  mediaCount,
  itemPosition,
  className,
  children,
  ...props
}: MediaGridItemProps & HTMLAttributes<HTMLElement>) => {
  return (
    <div
      className={cn(
        'bg-background-neutral-faded relative max-h-full min-h-full overflow-hidden rounded-lg',
        handleGridItemPosition(mediaCount, itemPosition),
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

export const ShowAllMedia = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      {...props}
      className={cn(
        'flex items-center gap-1 bg-white rounded-md px-4 py-2 leading-tight font-medium text-sm shadow-md max-h-8',
        className,
      )}
    >
      <IconFiles title="Show All Photos" width={16} height={16} />
      <span>Show All Photos</span>
    </div>
  );
};

export function maxMediaGridItems(mediaCount: MediaCount | number): MediaCount {
  return Math.min(mediaCount, MAX_MEDIA_COUNT) as MediaCount;
}

function handleGridLayout(mediaCount: MediaCount): string {
  switch (mediaCount) {
    case 2:
      return 'grid-cols-2';
    case 3:
      return 'grid-cols-[1fr_auto] grid-rows-2';
    case 4:
      return 'grid-cols-[1fr_auto_1fr] md:grid-cols-[1fr_auto_auto] lg:grid-cols-[1fr_auto_0.5fr]';
    default:
      return 'min-w-0 md:min-w-0'; // needs to override for 1 item
  }
}

function handleGridItemPosition(mediaCount: number, itemPosition: number): string {
  switch (mediaCount) {
    case 2:
      return 'aspect-square md:aspect-auto';

    case 3:
      if (itemPosition === 0)
        return 'aspect-square sm:aspect-[4/3] md:aspect-auto col-start-1 row-span-2 w-full min-w-full';
      if (itemPosition === 1 || itemPosition === 2) return 'aspect-square';
      return '';

    case 4:
      switch (itemPosition) {
        case 0:
          return 'col-start-1 row-span-2 aspect-square md:aspect-[3/4] w-full';
        case 1:
          return 'col-start-2 row-start-1 aspect-square h-full w-full';
        case 2:
          return 'col-start-2 row-start-2 aspect-square h-full w-full';
        case 3:
          return 'col-start-3 row-span-2 aspect-square md:aspect-[3/4] w-full';
        default:
          return '';
      }

    default:
      return '';
  }
}
